import { VehicleFragaResponse } from '@/core/domain/people/Vehicle/Vehicle.types';
import { Col, Modal, ModalProps, Row, Typography } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useWindowSize } from '@/hooks';

import './style.less';

const { Title, Text } = Typography;

interface Props extends ModalProps {
  onCancel?: VoidFunction;
  onConfirm?: (vehicle: VehicleFragaResponse) => void;
  vehicles?: VehicleFragaResponse[];
}

export default function VehicleModalConfirm(props: Props) {
  const { isMobile } = useWindowSize();
  const intl = useIntl().formatMessage;

  const [vehicleIndex, setVehicleIndex] = useState(0);

  const handleSelect = () => {
    if (props.vehicles?.[vehicleIndex]) {
      props.onConfirm?.(props.vehicles?.[vehicleIndex]);
    }
    props.onCancel?.();
    setVehicleIndex(0);
  };

  const referenceVehicle = props.vehicles?.[vehicleIndex];

  const highlightDifference = (value: string, reference?: string) => {
    const isDifferent = reference && value !== reference;

    return (
      <Text className="reference" mark={!!isDifferent}>
        {value}
      </Text>
    );
  };

  return (
    <Modal
      {...props}
      onCancel={handleSelect}
      onOk={handleSelect}
      width={800}
      title="Selecione o Veículo Correto"
      okText="Selecionar"
      destroyOnClose
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Foram encontrados múltiplos veículos para a mesma placa. Selecione o
          veículo correto abaixo:
        </Text>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Os campos destacados em <Text mark>amarelo</Text> são diferentes do
          veículo selecionado.
        </Text>
      </div>

      <div style={{ maxHeight: 600, overflow: 'auto' }}>
        {!!props.vehicles?.length &&
          props.vehicles.map((vehicle, index) => (
            <div
              className={`vehicle-modal-confirm-card ${
                index === vehicleIndex ? 'active' : ''
              }`}
              key={index}
              data-active={index === vehicleIndex}
              onClick={() => setVehicleIndex(index)}
            >
              <div className="confirm-content">
                <Row gutter={[12, 12]} align="middle" justify="center">
                  <Col span={24} sm={24} md={8}>
                    <img
                      src={
                        !!vehicle.image
                          ? vehicle.image
                          : `https://placehold.co/500x500?text=${vehicle.licensePlate}`
                      }
                      width={isMobile ? 250 : '100%'}
                      style={{ margin: 'auto', borderRadius: 8 }}
                    />
                  </Col>

                  <Col span={24} sm={12} md={8} className="vehicle-info">
                    <Title level={5} style={{ margin: 0 }}>
                      {vehicle.licensePlate}
                    </Title>

                    {[
                      {
                        label: 'Marca',
                        value: vehicle?.model?.brand?.name || '-',
                        reference: referenceVehicle?.model?.brand?.name || '-',
                      },
                      {
                        label: 'Modelo',
                        value: vehicle?.model?.name || '-',
                        reference: referenceVehicle?.model?.name || '-',
                      },
                      {
                        label: 'Chassi',
                        value: vehicle.chassi || '-',
                        reference: referenceVehicle?.chassi || '-',
                      },
                      {
                        label: 'Ano de Fabricação',
                        value: vehicle.yearFabrication.toString() || '-',
                        reference:
                          referenceVehicle?.yearFabrication.toString() || '-',
                      },
                      {
                        label: 'Ano do Modelo',
                        value: vehicle.yearModel.toString() || '-',
                        reference:
                          referenceVehicle?.yearModel.toString() || '-',
                      },
                      {
                        label: 'Combustível',
                        value: vehicle.fuel.name || '-',
                        reference: referenceVehicle?.fuel.name || '-',
                      },
                      {
                        label: 'Cor',
                        value: vehicle?.color.name
                          ? intl({
                              id: `customers.domain.vehicle.${vehicle?.color.name}`,
                            })
                          : '-',
                        reference: referenceVehicle?.color.name
                          ? intl({
                              id: `customers.domain.vehicle.${referenceVehicle?.color.name}`,
                            })
                          : '-',
                      },
                    ].map(({ label, value, reference }) => (
                      <Text type="secondary" key={label}>
                        {label}: {highlightDifference(value, reference)}
                      </Text>
                    ))}
                  </Col>

                  <Col span={24} sm={12} md={8} className="vehicle-info">
                    {[
                      {
                        label: 'Capacidade do Motor',
                        value: `${vehicle.engineCapacity} cc` || '-',
                        reference:
                          `${referenceVehicle?.engineCapacity} cc` || '-',
                      },
                      {
                        label: 'Válvulas',
                        value: vehicle.valves || '-',
                        reference: referenceVehicle?.valves || '-',
                      },
                      {
                        label: 'Cilindros',
                        value: vehicle.cylinders || '-',
                        reference: referenceVehicle?.cylinders || '-',
                      },
                      {
                        label: 'Motor',
                        value: vehicle.engineName || '-',
                        reference: referenceVehicle?.engineName || '-',
                      },
                      {
                        label: 'Codigo do motor',
                        value: vehicle.engineTechnicalCode || '-',
                        reference: referenceVehicle?.engineTechnicalCode || '-',
                      },
                      {
                        label: 'Potência',
                        value: vehicle.horsePower?.join(', ') || 'N/A',
                        reference:
                          referenceVehicle?.horsePower?.join(', ') || 'N/A',
                      },
                    ].map(({ label, value, reference }) => (
                      <Text type="secondary" key={label}>
                        {label}: {highlightDifference(value, reference)}
                      </Text>
                    ))}
                  </Col>
                </Row>
              </div>
            </div>
          ))}
      </div>
    </Modal>
  );
}
